import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import eventService from '../../services/eventService';
import paymentService from '../../services/paymentService';
import seatMapService from '../../services/seatMapService';
import api from '../../services/api';
import { getEcho } from '../../services/echo';
import CustomerSeatMap from '../../components/seatmap/CustomerSeatMap';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { Map, Marker } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';

const EventDetail = () => {
  const { id } = useParams();
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user }  = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTickets, setSelectedTickets] = useState({});
  const [activeTab, setActiveTab] = useState('tickets');
    const [topEvents, setTopEvents] = useState([]);
    const [topIndex, setTopIndex] = useState(0);
  const mapRef = useRef(null);
    const [pauseTopCarousel, setPauseTopCarousel] = useState(false);
    const [isPurchasing, setIsPurchasing] = useState(false);

  // Seat map state
  const [seatMap,        setSeatMap]        = useState(null);
  const [loadingSeatMap, setLoadingSeatMap] = useState(false);
  const [selectedSeats,  setSelectedSeats]  = useState([]);   // array of seat objects
  const [gaSelections,   setGaSelections]   = useState({});   // { [sectionId]: qty }
  const [queueCountdown, setQueueCountdown] = useState(0);
  const [isPurchaseMode, setIsPurchaseMode] = useState(!!location.state?.fromQueue);
  const queueCountdownRef  = useRef(null);
  const softHoldTimerRef   = useRef(null);
  const seatMapRef         = useRef(null); // always current, used in unmount cleanup

    useEffect(() => {
        if (!topEvents.length || pauseTopCarousel) return;

        const interval = setInterval(() => {
            setTopIndex(prev =>
                prev < topEvents.length - 1 ? prev + 1 : 0
            );
        }, 5000);

        return () => clearInterval(interval);
    }, [topEvents, pauseTopCarousel]);
  
  const MAPBOX_TOKEN = 'pk.eyJ1IjoidW5pdGVkc3lzdGVtYSIsImEiOiJjbWlsZWpnMXQwc2NlM2RzNW9wMWR4OWRwIn0.CP-OkFaPzS-Og8wtyZKleQ';

  // Parse geo_location to get latitude and longitude
  const getCoordinates = () => {
    if (!event || !event.geo_location) return null;
    
    const coords = event.geo_location.split(',');
    if (coords.length !== 2) return null;
    
    const latitude = parseFloat(coords[0]);
    const longitude = parseFloat(coords[1]);
    
    if (isNaN(latitude) || isNaN(longitude)) return null;
    
    return { latitude, longitude };
  };

  useEffect(() => {
    fetchEventDetail();
  }, [id]);

  // Subscribe to real-time inventory updates for this event
  useEffect(() => {
    let channel;
    try {
      const echo = getEcho();
      channel = echo.channel(`event.inventory.${id}`);
      channel.listen('.seat.status.updated', (e) => {
        if (parseInt(e.event_id) !== parseInt(id)) return;
        if (!e.seats?.length) return;
        const statusMap = {};
        e.seats.forEach(s => { statusMap[s.id] = s.status; });

        setSeatMap(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            sections: prev.sections?.map(sec => ({
              ...sec,
              seats: sec.seats?.map(seat =>
                statusMap[seat.id] !== undefined ? { ...seat, status: statusMap[seat.id] } : seat
              ),
            })),
          };
        });

        // Remove seats from selectedSeats if they are no longer selectable
        // (soft hold released by expiry, or sold by another user completing payment)
        const unavailableIds = e.seats
          .filter(s => s.status === 'available' || s.status === 'sold')
          .map(s => s.id);
        if (unavailableIds.length) {
          setSelectedSeats(prev => prev.filter(s => !unavailableIds.includes(s.id)));
        }
      });

      channel.listen('.inventory.updated', (e) => {
        if (parseInt(e.event_id) !== parseInt(id)) return;

        // Patch ticket category remaining counts
        if (e.ticket_categories?.length) {
          setEvent(prev => {
            if (!prev) return prev;
            const map = {};
            e.ticket_categories.forEach(u => { map[u.id] = u.remaining_tickets; });
            return {
              ...prev,
              ticket_categories: prev.ticket_categories?.map(tc =>
                map[tc.id] !== undefined ? { ...tc, remaining_tickets: map[tc.id] } : tc
              ),
            };
          });
        }

        // Patch GA section remaining capacity in the seat map
        if (e.ga_sections?.length) {
          setSeatMap(prev => {
            if (!prev) return prev;
            const map = {};
            e.ga_sections.forEach(u => { map[u.id] = u.remaining_capacity; });
            return {
              ...prev,
              sections: prev.sections?.map(s =>
                map[s.id] !== undefined ? { ...s, remaining_capacity: map[s.id] } : s
              ),
            };
          });
        }
      });
    } catch (_) {}

    return () => {
      try { getEcho().leave(`event.inventory.${id}`); } catch (_) {}
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start countdown timer when returning from WaitingRoom with an expiry time
  useEffect(() => {
    const expiresAt = location.state?.session_expires_at;
    if (!expiresAt) return;

    const tick = () => {
      const secs = Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000));
      setQueueCountdown(secs);
      if (secs <= 0) clearInterval(queueCountdownRef.current);
    };

    tick();
    queueCountdownRef.current = setInterval(tick, 1000);
    return () => clearInterval(queueCountdownRef.current);
  }, [location.state?.session_expires_at]); // eslint-disable-line react-hooks/exhaustive-deps


  // Keep seatMapRef current so unmount cleanup can read it without stale closure
  useEffect(() => { seatMapRef.current = seatMap; }, [seatMap]);

  // Debounced soft-hold sync: every time the seat selection changes, tell the
  // backend which seats this user is holding so other users see them as unavailable.
  useEffect(() => {
    if (!seatMap || !user || !isPurchaseMode) return; // only for seat-map events in purchase mode
    clearTimeout(softHoldTimerRef.current);
    softHoldTimerRef.current = setTimeout(async () => {
      try {
        await seatMapService.syncSoftHold(id, selectedSeats.map(s => s.id));
      } catch (err) {
        const data = err.response?.data;
        if (err.response?.status === 409 && data?.unavailable_seat_ids?.length) {
          setSelectedSeats(prev => prev.filter(s => !data.unavailable_seat_ids.includes(s.id)));
          toast.warn('One or more seats were just taken by another user and removed from your selection.');
        } else {
          console.warn('[soft-hold] sync failed', err.response?.status, err.response?.data);
        }
      }
    }, 400);
    return () => clearTimeout(softHoldTimerRef.current);
  }, [selectedSeats]); // eslint-disable-line react-hooks/exhaustive-deps

  // Release all soft holds when the user leaves the event page
  useEffect(() => {
    return () => {
      if (seatMapRef.current && user) {
        seatMapService.syncSoftHold(id, []).catch(() => {});
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEventDetail = async () => {
    try {
      setLoading(true);
      const response = await eventService.getEvent(id);

      const ev = response.data?.data || response.data;
      setEvent(ev);

      // Try to load a published seat map for this event
      setLoadingSeatMap(true);
      seatMapService.getEventSeatMap(id)
        .then(r => { if (r.data?.seat_map) setSeatMap(r.data.seat_map); })
        .catch(() => {})
        .finally(() => setLoadingSeatMap(false));
    } catch (error) {
      toast.error('Failed to load event details');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric',
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    const currency = event?.currency || 'USD';
    const locale = 'en-US'; // Use en-US for all to keep Latin numbers
    
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  const handleTicketQuantityChange = (ticketId, quantity) => {
    setSelectedTickets(prev => {
      if (quantity <= 0) {
        const { [ticketId]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [ticketId]: quantity };
    });
  };

  const getTotalAmount = () => {
    if (!event || !event.ticket_categories) return 0;
    
    return Object.entries(selectedTickets).reduce((total, [ticketId, quantity]) => {
      const ticket = event.ticket_categories.find(t => t.id === parseInt(ticketId));
      return total + (ticket ? ticket.price * quantity : 0);
    }, 0);
  };

  const getTotalTickets = () => {
    return Object.values(selectedTickets).reduce((sum, qty) => sum + qty, 0);
  };

    useEffect(() => {
        const fetchTopEvents = async () => {
            try {
                const res = await eventService.getTopEvents();
                setTopEvents(res.data.data || []);
            } catch (e) {
                console.error(e);
            }
        };

        fetchTopEvents();
    }, []);

  // Seat map handlers
  const handleSeatToggle = (seat) => {
    setSelectedSeats(prev => {
      const exists = prev.find(s => s.id === seat.id);
      return exists ? prev.filter(s => s.id !== seat.id) : [...prev, seat];
    });
  };

  const handleGaChange = (sectionId, qty) => {
    setGaSelections(prev => ({ ...prev, [sectionId]: qty }));
  };

  const getSeatMapTotal = () => {
    const seatTotal = selectedSeats.reduce((s, seat) => s + (seat.price || 0), 0);
    const gaTotal   = Object.entries(gaSelections).reduce((s, [secId, qty]) => {
      const section = seatMap?.sections?.find(sec => String(sec.id) === String(secId));
      const price   = section?.price_zone?.price || section?.default_price || 0;
      return s + price * qty;
    }, 0);
    return seatTotal + gaTotal;
  };

  const hasSeatMapSelection = () =>
    selectedSeats.length > 0 || Object.values(gaSelections).some(q => q > 0);

  const getSeatSection = (seat) =>
    seatMap?.sections?.find(sec => (sec.seats || []).some(s => s.id === seat.id));

  // Clicking "Purchase Now" — handles login check, queue check, then enters selection mode
  const handleEnterPurchaseMode = async () => {
    if (!user) {
      toast.info('Please login to purchase tickets');
      navigate('/login', { state: { from: `/events/${id}` } });
      return;
    }

    // Skip queue check when returning from the waiting room
    const fromQueue = location.state?.fromQueue;
    if (!fromQueue) {
      try {
        setIsPurchasing(true);
        const qRes = await api.get(`/events/${id}/queue/status`);
        if (qRes.data?.queue_enabled && qRes.data?.queue_status === 'active') {
          const tokenKey = `queue_token_${id}`;
          const existingToken = localStorage.getItem(tokenKey);
          const body = existingToken ? { session_token: existingToken } : {};

          const joinRes = await api.post(`/events/${id}/queue/join`, body);
          const data = joinRes.data?.data;

          if (data?.session_token) {
            localStorage.setItem(tokenKey, data.session_token);
          }

          if (data?.status === 'admitted' || data?.status === 'purchasing') {
            navigate(`/events/${id}/queue`);
            return;
          } else if (data?.status === 'waiting') {
            toast.info(`You're in the queue at position #${data.position}. Please wait.`);
            navigate(`/events/${id}/queue`);
            return;
          }
        }
      } catch (err) {
        if (err.response?.data?.queue_required) {
          navigate(`/events/${id}/queue`);
          return;
        }
      } finally {
        setIsPurchasing(false);
      }
    }

    setIsPurchaseMode(true);
  };

  // "Proceed to Checkout" — user has already selected; initiate payment
  const handleCheckout = async () => {
    // ── Seat-map checkout ─────────────────────────────────────────────────
    if (seatMap) {
      if (!hasSeatMapSelection()) {
        toast.warning('Please select at least one seat or General Admission ticket.');
        return;
      }

      try {
        setIsPurchasing(true);
        toast.info('Reserving seats and initiating payment…');

        const gaItems = Object.entries(gaSelections)
          .filter(([, qty]) => qty > 0)
          .map(([section_id, quantity]) => ({ section_id: parseInt(section_id), quantity }));

        const response = await seatMapService.holdSeats(id, {
          seat_ids: selectedSeats.map(s => s.id),
          ga_items: gaItems,
        });

        if (response.data.success && response.data.pay_url) {
          window.location.href = response.data.pay_url;
        } else {
          toast.error('Failed to initiate payment.');
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to initiate payment.');
      } finally {
        setIsPurchasing(false);
      }
      return;
    }

    // ── Standard ticket checkout ──────────────────────────────────────────
    const totalTickets = getTotalTickets();
    if (totalTickets === 0) {
      toast.warning('Please select at least one ticket');
      return;
    }

    const tickets = Object.entries(selectedTickets).map(([ticketId, quantity]) => ({
      ticket_category_id: parseInt(ticketId),
      quantity,
    }));

    try {
      setIsPurchasing(true);
      toast.info('Initiating payment...');

      const response = await paymentService.initiateTicketPayment({
        event_id: parseInt(id),
        tickets,
      });

      if (response.data.success && response.data.pay_url) {
        window.location.href = response.data.pay_url;
      } else {
        toast.error('Failed to initiate payment');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to initiate payment');
    } finally {
      setIsPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-16 w-16 mx-auto text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-600">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Event not found</h2>
          <Link to="/events" className="btn btn-primary">
            Back to Events
          </Link>
        </div>
      </div>
    );
  }

  const formatQueueCountdown = (secs) => {
    if (secs <= 0) return '00:00';
    const m = String(Math.floor(secs / 60)).padStart(2, '0');
    const s = String(secs % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <div className="min-h-screen">
      {/* Queue session countdown banner — portalled into the header so it sticks with the navbar */}
      {location.state?.fromQueue && location.state?.session_expires_at && (() => {
        const slot = document.getElementById('queue-banner-slot');
        if (!slot) return null;
        return ReactDOM.createPortal(
          <div className={`w-full py-2 px-4 text-center text-sm font-semibold text-white ${
            queueCountdown <= 30 ? 'bg-red-500' : queueCountdown <= 60 ? 'bg-orange-500' : 'bg-purple-600'
          }`}>
            {queueCountdown > 0
              ? <>Your purchase session expires in <span className="font-mono text-base">{formatQueueCountdown(queueCountdown)}</span> — complete your order before time runs out</>
              : 'Your purchase session has expired. Please rejoin the queue.'}
          </div>,
          slot
        );
      })()}

      {/* Hero Section with Event Image */}
      <div className="relative h-96 overflow-hidden">
          {event?.images && event.images.length > 0 ? (
              <img
                  src={`${process.env.REACT_APP_URL}/storage/${event.images[0].image_path}`}
                  alt={event.title}
                  className="w-full h-full object-cover"
              />
          ) : (
              <div className="w-full h-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500"></div>
          )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
        
        {/* Event Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <Link to="/events" className="text-white hover:text-gray-300 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </Link>
              {event.category && (
                <span className="badge bg-white/90 text-purple-900 backdrop-blur-sm">
                  {event.category.icon} {event.category.category_name}
                </span>
              )}
              {event.featured_event && (
                <span className="badge bg-purple-500 text-white">
                  ⭐ Featured
                </span>
              )}
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
              {event.title}
            </h1>
            <div className="flex flex-wrap gap-6 text-white">
              <div className="flex items-center">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{formatDate(event.event_start)}</span>
              </div>
              <div className="flex items-center">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
                <span>{event.location}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Event Details */}
          <div className={activeTab === 'tickets' ? 'lg:col-span-3' : 'lg:col-span-2'}>
            {/* Tabs */}
            <div className="mb-6 border-b border-gray-200">
              <nav className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('tickets')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'tickets'
                      ? 'border-purple-500 text-purple-500'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Tickets
                </button>
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'overview'
                      ? 'border-purple-500 text-purple-500'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('location')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'location'
                      ? 'border-purple-500 text-purple-500'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Location
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
              <div className="card p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">About This Event</h2>
                <div className="prose max-w-none">
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {event.description}
                  </p>
                </div>

                {/* Event Info Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8 pt-8 border-t border-gray-200">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Event Starts</h3>
                    <p className="text-lg text-gray-900">{formatDate(event.event_start)}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Event Ends</h3>
                    <p className="text-lg text-gray-900">{formatDate(event.event_end)}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Organized By</h3>
                    <p className="text-lg text-gray-900">{event.organizer?.business_name || event.organizer?.email || 'Event Organizer'}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Category</h3>
                    <p className="text-lg text-gray-900">{event.category?.icon} {event.category?.category_name}</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'tickets' && (
              <div className={`card p-8 ${isPurchaseMode ? 'pb-28' : 'pb-8'}`}>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {isPurchaseMode ? 'Select Tickets' : 'Tickets'}
                </h2>
                {!isPurchaseMode && (
                  <p className="text-sm text-gray-500 mb-6">Browse available tickets below, then click <strong>Purchase Now</strong> to start your order.</p>
                )}
                {isPurchaseMode && <div className="mb-6" />}

                {/* ── Seat Map ── */}
                {loadingSeatMap && (
                  <div className="text-center py-8 text-gray-500 text-sm">Loading seat map…</div>
                )}

                {seatMap && !loadingSeatMap && (
                  <div className="mb-8">
                    <CustomerSeatMap
                      seatMap={seatMap}
                      selectedSeats={selectedSeats}
                      onSeatToggle={handleSeatToggle}
                      gaSelections={gaSelections}
                      onGaChange={handleGaChange}
                      viewOnly={!isPurchaseMode}
                    />
                  </div>
                )}

                {/* ── Standard ticket categories (hidden when seat map is active) ── */}
                {!seatMap && !loadingSeatMap && event.ticket_categories && event.ticket_categories.length > 0 ? (
                  <div className="space-y-4">
                    {event.ticket_categories.map((ticket) => (
                      <div key={ticket.id} className="border border-gray-200 rounded-lg p-6 hover:border-purple-500 transition-colors">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">{ticket.category_name}</h3>
                            <p className="text-sm text-gray-600 mb-3">{ticket.description}</p>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="font-semibold text-purple-500 text-xl">{formatPrice(ticket.price)}</span>
                              <span className="text-gray-500">
                                {ticket.remaining_tickets > 0
                                  ? `${ticket.remaining_tickets} tickets remaining`
                                  : 'Sold Out'}
                              </span>
                            </div>
                          </div>
                          {isPurchaseMode && ticket.remaining_tickets > 0 && (
                            <div className="flex items-center gap-2 ml-4">
                              <button
                                onClick={() => handleTicketQuantityChange(ticket.id, (selectedTickets[ticket.id] || 0) - 1)}
                                disabled={!selectedTickets[ticket.id] || selectedTickets[ticket.id] === 0}
                                className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-purple-700 hover:text-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" />
                                </svg>
                              </button>
                              <span className="w-12 text-center font-semibold text-lg">
                                {selectedTickets[ticket.id] || 0}
                              </span>
                              <button
                                onClick={() => handleTicketQuantityChange(ticket.id, (selectedTickets[ticket.id] || 0) + 1)}
                                disabled={selectedTickets[ticket.id] >= Math.min(ticket.remaining_tickets, 10)}
                                className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-purple-500 hover:text-purple-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  !seatMap && !loadingSeatMap && (
                    <p className="text-gray-500 text-center py-8">No tickets available for this event</p>
                  )
                )}

                {/* ── Purchase Now button (view mode only) ── */}
                {!isPurchaseMode && !loadingSeatMap && (
                  <div className="mt-8 flex justify-center">
                    <button
                      onClick={handleEnterPurchaseMode}
                      disabled={isPurchasing}
                      className={`btn !bg-purple-500 text-white font-bold px-16 py-4 text-lg rounded-xl shadow-lg hover:!bg-purple-600 transition-colors ${isPurchasing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isPurchasing ? 'Please wait…' : 'Purchase Now'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'location' && (() => {
              const coords = getCoordinates();
              
              return (
                <div className="card p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Event Location</h2>
                  <div className="flex items-start mb-6">
                    <svg className="w-6 h-6 text-purple-500 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      <p className="text-lg font-semibold text-gray-900">{event.location}</p>
                      {coords && (
                        <p className="text-sm text-gray-500 mt-1">
                          Coordinates: {coords.latitude.toFixed(6)}, {coords.longitude.toFixed(6)}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {coords ? (
                    <div className="rounded-lg overflow-hidden" style={{ height: '400px' }}>
                      <Map
                        ref={mapRef}
                        mapboxAccessToken={MAPBOX_TOKEN}
                        initialViewState={{
                          longitude: coords.longitude,
                          latitude: coords.latitude,
                          zoom: 14
                        }}
                        style={{ width: '100%', height: '100%' }}
                        mapStyle="mapbox://styles/mapbox/streets-v12"
                      >
                        <Marker
                          longitude={coords.longitude}
                          latitude={coords.latitude}
                          anchor="bottom"
                        >
                          <div className="flex flex-col items-center">
                            <div className="bg-purple-500 text-white p-2 rounded-lg shadow-lg">
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </div>
                            <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-purple-500"></div>
                          </div>
                        </Marker>
                      </Map>
                    </div>
                  ) : (
                    <div className="bg-gray-100 rounded-lg h-96 flex items-center justify-center">
                      <p className="text-gray-500">Location coordinates not available</p>
                    </div>
                  )}
                </div>
              );
            })()}
              {topEvents.length > 0 && (
                  <section className="w-full px-4 sm:px-6 lg:px-8 mt-10 mb-12">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-4 max-w-7xl mx-auto">
                          <h2 className="text-xl font-bold text-gray-900">
                              Top Events
                          </h2>

                          {/* Arrows */}
                          <div className="flex gap-2">
                              <button
                                  onClick={() => {
                                      setPauseTopCarousel(true);
                                      setTopIndex(i => Math.max(i - 1, 0));
                                  }}
                                  disabled={topIndex === 0}
                                  className="w-9 h-9 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-40 flex items-center justify-center"
                              >
                                  ‹
                              </button>

                              <button
                                  onClick={() => {
                                      setPauseTopCarousel(true);
                                      setTopIndex(i => Math.min(i + 1, topEvents.length - 1));
                                  }}
                                  disabled={topIndex >= topEvents.length - 1}
                                  className="w-9 h-9 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-40 flex items-center justify-center"
                              >
                                  ›
                              </button>
                          </div>
                      </div>

                      {/* Carousel */}
                      {/* IMPORTANT: overflow-x-hidden ONLY */}
                      <div className="overflow-x-hidden rounded-xl shadow-md" onMouseEnter={() => setPauseTopCarousel(true)}
                           onMouseLeave={() => setPauseTopCarousel(false)}>
                          <div
                              className="flex transition-transform duration-500 max-w-7xl mx-auto"
                              style={{
                                  transform: `translateX(-${topIndex * 100}%)`,
                              }}
                          >
                              {topEvents.map(event => (
                                  <Link
                                      key={event.id}
                                      to={`/events/${event.id}`}
                                      className="min-w-full bg-white rounded-xl border border-gray-200 shadow-lg hover:shadow-2xl transition flex flex-col md:flex-row overflow-hidden lg:h-[280px] no-underline hover:no-underline"
                                  >
                                      {/* Image */}
                                      <img
                                          src={
                                              event?.images && event.images.length > 0
                                                  ? `${process.env.REACT_APP_URL}/storage/${event.images[0].image_path}`
                                                  : 'https://via.placeholder.com/600x400?text=Event'
                                          }
                                          alt={event.title}
                                          className="w-full h-64 sm:h-72 md:w-1/2 lg:h-full object-cover"
                                          onError={e => {
                                              e.target.onerror = null;
                                              e.target.src = 'https://via.placeholder.com/600x400?text=Event';
                                          }}
                                      />

                                      {/* Content */}
                                      <div className="p-4 sm:p-6 flex flex-col justify-between md:w-1/2">
                                          <div>
                                              <h3 className="font-extrabold text-gray-900 text-lg line-clamp-2">
                                                  {event.title}
                                              </h3>

                                              <div className="space-y-1 mt-3">
                                                  {/* Location */}
                                                  <div className="flex items-center text-sm text-gray-900">
                                                      <svg
                                                          className="w-4 h-4 mr-1 text-purple-500"
                                                          fill="none"
                                                          stroke="currentColor"
                                                          viewBox="0 0 24 24"
                                                      >
                                                          <path
                                                              strokeLinecap="round"
                                                              strokeLinejoin="round"
                                                              strokeWidth="2"
                                                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                                          />
                                                          <path
                                                              strokeLinecap="round"
                                                              strokeLinejoin="round"
                                                              strokeWidth="2"
                                                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                                          />
                                                      </svg>
                                                      <span>{event.location}</span>
                                                  </div>

                                                  {/* Date */}
                                                  <div className="flex items-center text-sm text-gray-900">
                                                      <svg
                                                          className="w-4 h-4 mr-1 text-purple-500"
                                                          fill="none"
                                                          stroke="currentColor"
                                                          viewBox="0 0 24 24"
                                                      >
                                                          <path
                                                              strokeLinecap="round"
                                                              strokeLinejoin="round"
                                                              strokeWidth="2"
                                                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                          />
                                                      </svg>
                                                      <span>{formatDate(event.event_start)}</span>
                                                  </div>

                                                  {/* Cheapest price */}
                                                  {event.ticket_categories?.length > 0 && (
                                                      <div className="flex items-center text-sm text-gray-900 mt-1">
                                                          <svg
                                                              xmlns="http://www.w3.org/2000/svg"
                                                              className="w-5 h-5 mr-1"
                                                              viewBox="0 0 24 24"
                                                              fill="#8B5CF6"
                                                          >
                                                              <path d="M9 10a1 1 0 0 0-1 1v2a1 1 0 0 0 2 0v-2a1 1 0 0 0-1-1Zm12 1a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1a1 1 0 0 1 0 2a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1a1 1 0 0 1 0-2Zm-1-1.82a3 3 0 0 0 0 5.64V17H10a1 1 0 0 0-2 0H4v-2.18a3 3 0 0 0 0-5.64V7h4a1 1 0 0 0 2 0h10Z" />
                                                          </svg>

                                                          <span>
                        From {Math.min(...event.ticket_categories.map(tc => tc.price))} {event.currency}
                      </span>
                                                      </div>
                                                  )}
                                              </div>
                                          </div>

                                          <span className="text-purple-500 font-semibold mt-4">
                View →
              </span>
                                      </div>
                                  </Link>
                              ))}
                          </div>
                      </div>
                  </section>
              )}
          </div>

          {/* Right Column - Purchase Card (hidden on tickets tab; summary moves to floating bar) */}
          {activeTab !== 'tickets' && <div className="lg:col-span-1">
            <div className="card p-6 sticky top-24">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Ticket Summary</h3>
              
              {(seatMap ? hasSeatMapSelection() : getTotalTickets() > 0) ? (
                <div className="space-y-4">
                  {/* Seat map selection summary */}
                  {seatMap && selectedSeats.map(s => (
                    <div key={s.id} className="flex justify-between items-center py-2 border-b border-gray-200">
                      <div>
                        <p className="font-medium text-gray-900">Seat {s.label}</p>
                        <p className="text-sm text-gray-500">{s.price_zone?.name || 'Standard'}</p>
                      </div>
                      <p className="font-semibold text-gray-900">{formatPrice(s.price)}</p>
                    </div>
                  ))}
                  {seatMap && Object.entries(gaSelections).filter(([, q]) => q > 0).map(([secId, qty]) => {
                    const section = seatMap.sections?.find(s => String(s.id) === String(secId));
                    const price   = section?.price_zone?.price || section?.default_price || 0;
                    return (
                      <div key={secId} className="flex justify-between items-center py-2 border-b border-gray-200">
                        <div>
                          <p className="font-medium text-gray-900">{section?.name || 'GA'}</p>
                          <p className="text-sm text-gray-500">{qty} × {formatPrice(price)}</p>
                        </div>
                        <p className="font-semibold text-gray-900">{formatPrice(price * qty)}</p>
                      </div>
                    );
                  })}

                  {/* Standard ticket list */}
                  {!seatMap && event.ticket_categories && event.ticket_categories
                    .filter(ticket => selectedTickets[ticket.id] > 0)
                    .map(ticket => (
                      <div key={ticket.id} className="flex justify-between items-center py-2 border-b border-gray-200">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{ticket.category_name}</p>
                          <p className="text-sm text-gray-500">{selectedTickets[ticket.id]} × {formatPrice(ticket.price)}</p>
                        </div>
                        <p className="font-semibold text-gray-900">
                          {formatPrice(ticket.price * selectedTickets[ticket.id])}
                        </p>
                      </div>
                    ))}

                  {/* Total */}
                  <div className="pt-4 border-t-2 border-gray-300">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-lg font-semibold text-gray-900">Total</span>
                      <span className="text-2xl font-bold text-purple-500">
                        {formatPrice(seatMap ? getSeatMapTotal() : getTotalAmount())}
                      </span>
                    </div>
                    {!seatMap && (
                      <p className="text-sm text-gray-500">{getTotalTickets()} ticket{getTotalTickets() > 1 ? 's' : ''}</p>
                    )}
                  </div>

                  {/* Purchase Button */}
                  <button onClick={handleCheckout} disabled={isPurchasing}
                    className={`btn !bg-purple-500 text-white text-semibold w-full mt-6 py-4 text-lg font-semibold ${
                      isPurchasing ? 'opacity-50 cursor-not-allowed' : ''
                    }`}>
                    {isPurchasing ? 'Processing...' : 'Proceed to Checkout'}
                  </button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                  <p className="text-gray-500 mb-2">No tickets selected</p>
                  <p className="text-sm text-gray-400">
                    {seatMap ? 'Select seats from the map above' : 'Select tickets from the Tickets tab'}
                  </p>
                </div>
              )}

              {/* Event Stats */}
              <div className="mt-8 pt-6 border-t border-gray-200 space-y-3">
                {/*<div className="flex items-center text-sm text-gray-600">*/}
                {/*  <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">*/}
                {/*    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />*/}
                {/*  </svg>*/}
                {/*  {event.ticket_categories ? */}
                {/*    event.ticket_categories.reduce((sum, t) => sum + (t.quantity - t.remaining_tickets), 0) : 0*/}
                {/*  } tickets sold*/}
                {/*</div>*/}
                <div className="flex items-center text-sm text-gray-600">
                  <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Sale ends {formatDate(event.sale_end)}
                </div>
              </div>
            </div>
          </div>}
        </div>
      </div>

      {/* ── Floating bottom bar — visible on tickets tab in purchase mode when something is selected ── */}
      {activeTab === 'tickets' && isPurchaseMode && (seatMap ? hasSeatMapSelection() : getTotalTickets() > 0) && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50 }}
          className="bg-white border-t border-gray-200 shadow-2xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center gap-4">
              {/* Selected items */}
              <div className="flex-1 flex items-center gap-3 overflow-x-auto min-w-0">
                {seatMap && selectedSeats.map(s => {
                  const sec = getSeatSection(s);
                  return (
                    <span key={s.id}
                      className="flex-shrink-0 bg-purple-500 text-white text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1">
                      {sec ? `${sec.name} · ` : ''}{s.label}
                      <button onClick={() => handleSeatToggle(s)}
                        className="ml-1 text-purple-100 hover:text-white leading-none">✕</button>
                    </span>
                  );
                })}
                {seatMap && Object.entries(gaSelections).filter(([, q]) => q > 0).map(([secId, qty]) => {
                  const section = seatMap.sections?.find(s => String(s.id) === String(secId));
                  const remaining = section?.remaining_capacity ?? section?.capacity ?? 0;
                  const price = section?.price_zone?.price || section?.default_price || 0;
                  return (
                    <div key={secId}
                      className="flex-shrink-0 flex items-center gap-2 bg-gray-100 border border-gray-200 rounded-xl px-3 py-1.5">
                      <div className="flex flex-col leading-tight">
                        <span className="text-xs font-semibold text-gray-800">{section?.name || 'GA Zone'}</span>
                        <span className="text-[10px] text-purple-700 font-medium uppercase tracking-wide">GA Zone{price ? ` · QAR ${price}` : ''}</span>
                      </div>
                      <div className="flex items-center gap-1.5 ml-1">
                        <button
                          onClick={() => handleGaChange(secId, Math.max(0, qty - 1))}
                          className="w-6 h-6 rounded-full bg-white border border-gray-300 hover:bg-gray-200 text-gray-700 font-bold text-sm flex items-center justify-center leading-none">
                          −
                        </button>
                        <span className="w-5 text-center text-sm font-bold text-gray-900">{qty}</span>
                        <button
                          onClick={() => handleGaChange(secId, Math.min(remaining, qty + 1))}
                          className="w-6 h-6 rounded-full bg-white border border-gray-300 hover:bg-gray-200 text-gray-700 font-bold text-sm flex items-center justify-center leading-none">
                          +
                        </button>
                      </div>
                    </div>
                  );
                })}
                {!seatMap && event.ticket_categories && event.ticket_categories
                  .filter(t => selectedTickets[t.id] > 0)
                  .map(t => (
                    <span key={t.id}
                      className="flex-shrink-0 bg-gray-100 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full">
                      {t.category_name} × {selectedTickets[t.id]}
                    </span>
                  ))}
              </div>

              {/* Total + checkout */}
              <div className="flex items-center gap-4 flex-shrink-0">
                <div className="text-right">
                  <p className="text-xs text-gray-500">Total</p>
                  <p className="text-xl font-bold text-purple-500">
                    {formatPrice(seatMap ? getSeatMapTotal() : getTotalAmount())}
                  </p>
                  {location.state?.fromQueue && queueCountdown > 0 && (
                    <p className={`text-xs font-mono font-semibold ${queueCountdown <= 30 ? 'text-red-500' : 'text-orange-500'}`}>
                      {formatQueueCountdown(queueCountdown)} left
                    </p>
                  )}
                </div>
                <button onClick={handleCheckout} disabled={isPurchasing}
                  className={`btn !bg-purple-500 text-white font-semibold px-8 py-3 text-base whitespace-nowrap ${
                    isPurchasing ? 'opacity-50 cursor-not-allowed' : ''
                  }`}>
                  {isPurchasing ? 'Processing…' : 'Proceed to Checkout'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetail;

